import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ============================================
// TYPES
// ============================================

export type HealthStatus = 'HEALTHY' | 'WARNING' | 'CRITICAL';

export type AlertModule = 
  | 'TOKENIZATION' 
  | 'BANKING' 
  | 'CREDIT' 
  | 'KYC' 
  | 'CONTRACTS' 
  | 'SECURITY' 
  | 'INFRASTRUCTURE';

export type AlertSeverity = 'INFO' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface ModuleHealth {
  module: string;
  status: HealthStatus;
  metrics: Record<string, number>;
  alerts: number;
  lastCheck: string;
}

export interface SystemHealthSummary {
  overallStatus: HealthStatus;
  modules: {
    tokenization: ModuleHealth;
    banking: ModuleHealth;
    credit: ModuleHealth;
    kyc: ModuleHealth;
    contracts: ModuleHealth;
    security: ModuleHealth;
  };
  totalAlerts: number;
  criticalAlerts: number;
  lastUpdated: string;
}

export interface SystemAlert {
  id: string;
  tenantId?: string;
  module: AlertModule;
  type: string;
  severity: AlertSeverity;
  title: string;
  message: string;
  entityId?: string;
  entityType?: string;
  data?: Record<string, unknown>;
  resolution?: string;
  acknowledged: boolean;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
}

export interface AlertStats {
  total: number;
  byModule: Record<AlertModule, number>;
  bySeverity: Record<AlertSeverity, number>;
  unacknowledged: number;
  unresolved: number;
}

export interface AlertFilters {
  module?: AlertModule;
  severity?: AlertSeverity;
  acknowledged?: boolean;
  resolved?: boolean;
}

// ============================================
// HEALTH HOOKS
// ============================================

export function useSystemHealth(tenantId?: string) {
  return useQuery({
    queryKey: ['system-health', tenantId],
    queryFn: async () => {
      const params = tenantId ? { tenantId } : {};
      const { data } = await api.get<SystemHealthSummary>('/monitoring/health', { params });
      return data;
    },
    refetchInterval: 30000, // Atualiza a cada 30 segundos
  });
}

export function useDatabaseHealth() {
  return useQuery({
    queryKey: ['database-health'],
    queryFn: async () => {
      const { data } = await api.get('/monitoring/health/database');
      return data;
    },
    refetchInterval: 60000, // Atualiza a cada minuto
  });
}

export function useTokenizationHealth() {
  return useQuery({
    queryKey: ['tokenization-health'],
    queryFn: async () => {
      const { data } = await api.get<ModuleHealth>('/monitoring/health/tokenization');
      return data;
    },
    refetchInterval: 30000,
  });
}

export function useBankingHealth() {
  return useQuery({
    queryKey: ['banking-health'],
    queryFn: async () => {
      const { data } = await api.get<ModuleHealth>('/monitoring/health/banking');
      return data;
    },
    refetchInterval: 30000,
  });
}

export function useCreditHealth() {
  return useQuery({
    queryKey: ['credit-health'],
    queryFn: async () => {
      const { data } = await api.get<ModuleHealth>('/monitoring/health/credit');
      return data;
    },
    refetchInterval: 30000,
  });
}

export function useKycHealth() {
  return useQuery({
    queryKey: ['kyc-health'],
    queryFn: async () => {
      const { data } = await api.get<ModuleHealth>('/monitoring/health/kyc');
      return data;
    },
    refetchInterval: 30000,
  });
}

export function useContractsHealth() {
  return useQuery({
    queryKey: ['contracts-health'],
    queryFn: async () => {
      const { data } = await api.get<ModuleHealth>('/monitoring/health/contracts');
      return data;
    },
    refetchInterval: 30000,
  });
}

export function useSecurityHealth() {
  return useQuery({
    queryKey: ['security-health'],
    queryFn: async () => {
      const { data } = await api.get<ModuleHealth>('/monitoring/health/security');
      return data;
    },
    refetchInterval: 30000,
  });
}

// ============================================
// ALERT HOOKS
// ============================================

export function useSystemAlerts(filters?: AlertFilters) {
  return useQuery({
    queryKey: ['system-alerts', filters],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters?.module) params.module = filters.module;
      if (filters?.severity) params.severity = filters.severity;
      if (filters?.acknowledged !== undefined) params.acknowledged = String(filters.acknowledged);
      if (filters?.resolved !== undefined) params.resolved = String(filters.resolved);
      
      const { data } = await api.get<SystemAlert[]>('/monitoring/alerts', { params });
      return data;
    },
    refetchInterval: 15000, // Atualiza a cada 15 segundos
  });
}

export function useAlertStats() {
  return useQuery({
    queryKey: ['alert-stats'],
    queryFn: async () => {
      const { data } = await api.get<AlertStats>('/monitoring/alerts/stats');
      return data;
    },
    refetchInterval: 30000,
  });
}

export function useCriticalAlerts() {
  return useQuery({
    queryKey: ['critical-alerts'],
    queryFn: async () => {
      const { data } = await api.get<SystemAlert[]>('/monitoring/alerts/critical');
      return data;
    },
    refetchInterval: 10000, // Atualiza a cada 10 segundos para alertas críticos
  });
}

export function useUnacknowledgedAlertCount(module?: AlertModule) {
  return useQuery({
    queryKey: ['unacknowledged-alert-count', module],
    queryFn: async () => {
      const params = module ? { module } : {};
      const { data } = await api.get<{ count: number }>('/monitoring/alerts/count', { params });
      return data.count;
    },
    refetchInterval: 15000,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (alertId: string) => {
      const { data } = await api.patch<SystemAlert>(`/monitoring/alerts/${alertId}/acknowledge`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      queryClient.invalidateQueries({ queryKey: ['critical-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['unacknowledged-alert-count'] });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ alertId, resolution }: { alertId: string; resolution?: string }) => {
      const { data } = await api.patch<SystemAlert>(`/monitoring/alerts/${alertId}/resolve`, { resolution });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      queryClient.invalidateQueries({ queryKey: ['critical-alerts'] });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

export function getStatusColor(status: HealthStatus): string {
  switch (status) {
    case 'HEALTHY':
      return 'text-green-600 bg-green-100';
    case 'WARNING':
      return 'text-yellow-600 bg-yellow-100';
    case 'CRITICAL':
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

export function getSeverityColor(severity: AlertSeverity): string {
  switch (severity) {
    case 'INFO':
      return 'text-blue-600 bg-blue-100';
    case 'LOW':
      return 'text-green-600 bg-green-100';
    case 'MEDIUM':
      return 'text-yellow-600 bg-yellow-100';
    case 'HIGH':
      return 'text-orange-600 bg-orange-100';
    case 'CRITICAL':
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

export function getModuleIcon(module: AlertModule): string {
  switch (module) {
    case 'TOKENIZATION':
      return '🪙';
    case 'BANKING':
      return '🏦';
    case 'CREDIT':
      return '💳';
    case 'KYC':
      return '🔍';
    case 'CONTRACTS':
      return '📄';
    case 'SECURITY':
      return '🔒';
    case 'INFRASTRUCTURE':
      return '🖥️';
    default:
      return '⚙️';
  }
}

export function formatModuleName(module: string): string {
  const names: Record<string, string> = {
    tokenization: 'Tokenização',
    banking: 'Banking',
    credit: 'Crédito',
    kyc: 'KYC',
    contracts: 'Contratos',
    security: 'Segurança',
    infrastructure: 'Infraestrutura',
  };
  return names[module.toLowerCase()] || module;
}
